import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React from 'react'
import CheckFilledLoD from 'src/assets/svgs/CheckFilledLoD.svg'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Spotlight from 'src/components/Spotlight/Spotlight'
import SvgClose from 'src/components/Svgs/SvgClose'
import { LocationState } from 'src/redux/createRootReducer'
import { css } from 'styled-components'

export enum CtaDesigns {
  button = 'button',
  hoverableLink = 'hoverableLink',
}

interface PromotionBoxProps {
  className?: string
  imageSrc: string
  title: React.ReactElement
  id?: string
  subtitle: React.ReactElement
  ctaProps: {
    ctaDesign: CtaDesigns
    ctaContent: React.ReactElement
    ctaTo: string | Location<LocationState>
    ctaAriaLabelSource: string
    onCtaClick?: () => void
  }
  tooltipProps?: {
    tooltipText: React.ReactElement
    tooltipTitle: React.ReactElement
    showTooltip: boolean
    setShowTooltip: (val: boolean) => void
  }
  dismissPromo?: () => void
}

// TODO: refactor out all usages of PermanentPromotionBox
const PromotionBox: React.FC<PromotionBoxProps> = (
  props,
): React.ReactElement => {
  const handleOnClick = (): void => {
    if (props.ctaProps?.onCtaClick !== undefined) {
      props.ctaProps?.onCtaClick()
    }
    if (props.tooltipProps?.setShowTooltip !== undefined) {
      props.tooltipProps?.setShowTooltip(false)
    }
  }

  const cta = (ctaDesign: CtaDesigns): React.ReactElement => {
    switch (ctaDesign) {
      case CtaDesigns.hoverableLink:
        return (
          <HoverableLink
            id={props.id ? props.id + 'Cta' : 'cta'}
            onClick={(): void => {
              handleOnClick()
            }}
            css={css`
              display: flex;
              align-items: center;
            `}
            to={props.ctaProps?.ctaTo}
            ariaLabelSource={props.ctaProps?.ctaAriaLabelSource}
          >
            {props.ctaProps?.ctaContent}
          </HoverableLink>
        )
      case CtaDesigns.button:
      default: {
        return (
          <Button
            id={props.id ? props.id + 'Cta' : 'cta'}
            onClick={handleOnClick}
            linkProps={{
              to: props.ctaProps?.ctaTo,
              ariaLabelSource: props.ctaProps?.ctaAriaLabelSource,
            }}
            design={ButtonDesigns.designCta}
            type={ButtonTypes.button}
            text={props.ctaProps?.ctaContent}
            big
            css={css`
              width: fit-content;
            `}
          />
        )
      }
    }
  }

  return (
    <Box
      css={css`
        position: relative;
        display: flex;
        ${getBreakpointOnly(breakpointSizes.MD)(css`
          padding: 20px;
        `)}
      `}
      className={props.className}
      isHighlighted={props.tooltipProps?.showTooltip}
    >
      {props.dismissPromo !== undefined && (
        <SvgClose
          css={css`
            position: absolute;
            top: 10px;
            right: 10px;
            cursor: pointer;
          `}
          onClick={props.dismissPromo}
        />
      )}
      <img
        css={css`
          width: 110px;
          height: 110px;
          ${getBreakpointUp(breakpointSizes.SM)(css`
            flex-shrink: 0;
          `)}
          ${getBreakpointOnly(breakpointSizes.LG)(css`
            height: 70px;
            width: 70px;
          `)}
          ${getBreakpointOnly(breakpointSizes.MD)(css`
            height: 50px;
            width: 50px;
          `)}
        `}
        src={props.imageSrc}
        alt={''}
      />
      <div
        css={css`
          margin-left: 40px;
          display: flex;
          flex-direction: column;
          ${getBreakpointDown(breakpointSizes.LG)(css`
            margin-left: 20px;
          `)}
        `}
      >
        <span
          id={props.id ? props.id : 'headerText'}
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 12px;
          `}
        >
          {props.title}
        </span>
        <span
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 24px;
          `}
        >
          {props.subtitle}
        </span>
        <div>
          {cta(props.ctaProps?.ctaDesign)}
          {props.tooltipProps?.showTooltip && (
            <Spotlight
              imageSrc={CheckFilledLoD}
              tooltip
              smallerText
              title={props.tooltipProps?.tooltipTitle}
              subtitle={props.tooltipProps?.tooltipText}
              cookieFunction={props.tooltipProps?.setShowTooltip}
              css={css`
                display: flex;
                position: absolute;
                z-index: 1;
                margin-top: 10px;
                min-width: 200px;
                max-width: 350px;
              `}
            />
          )}
        </div>
      </div>
    </Box>
  )
}

export default PromotionBox
